import unescape from 'lodash/unescape';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { rewriteImageUrl } from '../../../helpers';
import { TopLevelCategories } from '../../../enums';
import { ProductTypeSlugs, TopLevelCategoryBasePath } from '../../../constants';
import Icons from '../../../../../common/widgets/Icons';
import {
  HomeShortcut,
  HomeShortcutAction,
  RedirectTypes,
} from '../../../models/Categories';
import Carousel from '../../../../../common/widgets/Carousel';

const SHORTCUT_MIN_WIDTH = 290;
const SHORTCUT_GAP = 24;
const SHORTCUT_CAROUSEL_GAP = SHORTCUT_GAP;
const MAX_VISIBLE_SHORTCUTS = 3;

const getVisibleShortcutCount = (
  containerWidth: number,
  gap = SHORTCUT_GAP,
  outerMargin = SHORTCUT_GAP,
) =>
  Math.min(
    MAX_VISIBLE_SHORTCUTS,
    Math.max(
      1,
      Math.floor((containerWidth + outerMargin) / (SHORTCUT_MIN_WIDTH + gap)),
    ),
  );

const getActionUrl = (action: HomeShortcutAction): string | undefined => {
  switch (action.type) {
    case RedirectTypes.FlexibleBenefits:
      return action.goTo && action.goTo !== TopLevelCategories.FlexibleBenefits
        ? `/flexible-benefits/${action.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.FlexibleBenefits];
    case RedirectTypes.ClubBenefits:
      return action.goTo && action.goTo !== TopLevelCategories.BenefitClub
        ? `/club-benefits/${action.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.BenefitClub];
    case RedirectTypes.Browse:
      return ``;
    case RedirectTypes.Product:
      return action.goTo ? `/product/${action.goTo}` : undefined;
    case RedirectTypes.EdenredCards:
      return `/my-account/cards`;
    case RedirectTypes.ProductType:
      return `/products/type/${ProductTypeSlugs[action.goTo ?? 0]}`;
    case RedirectTypes.MyBudget:
      return '/my-account/dashboard';
    case RedirectTypes.Cashback:
      return action.goTo && action.goTo !== TopLevelCategories.Cashback
        ? `/cashback/${action.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.Cashback];
    case RedirectTypes.DiscountVouchers:
      return action.goTo && action.goTo !== TopLevelCategories.DiscountVouchers
        ? `/discount-vouchers/${action.goTo}`
        : TopLevelCategoryBasePath[TopLevelCategories.DiscountVouchers];
    case RedirectTypes.ActiveBenefits:
      return '/my-account/my-benefits';
    default:
      return undefined;
  }
};

const ShortcutAction = ({
  action,
  secondary = false,
}: {
  action: HomeShortcutAction | null;
  secondary?: boolean;
}) => {
  const url = action && getActionUrl(action);
  if (!url || !action?.label) return null;

  return (
    <Link
      to={url}
      className={`btn ${
        secondary
          ? 'btn-outline homepage-shortcut-action--secondary'
          : 'btn-solid'
      } homepage-shortcut-action`}
      aria-label={unescape(action.label)}
    >
      {unescape(action.label)}
      {secondary && <Icons.ArrowRight />}
    </Link>
  );
};

export const Shortcuts = ({ shortcuts }: { shortcuts: HomeShortcut[] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const visibleGridShortcutCount = containerWidth
    ? getVisibleShortcutCount(containerWidth)
    : Math.min(shortcuts.length, MAX_VISIBLE_SHORTCUTS);
  const showCarousel = shortcuts.length > visibleGridShortcutCount;
  const visibleCarouselShortcutCount = containerWidth
    ? getVisibleShortcutCount(
        containerWidth,
        SHORTCUT_CAROUSEL_GAP,
        SHORTCUT_GAP,
      )
    : Math.min(shortcuts.length, MAX_VISIBLE_SHORTCUTS);
  const visibleShortcutCount = showCarousel
    ? visibleCarouselShortcutCount
    : visibleGridShortcutCount;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () =>
      setContainerWidth(container.getBoundingClientRect().width);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const cards = shortcuts.map((shortcut, index) => (
    <article
      className="homepage-shortcut-card"
      key={`${shortcut.shortcutType}-${index}`}
    >
      <div className="homepage-shortcut-content">
        <div
          className={`homepage-shortcut-heading${
            shortcut.secondaryImage
              ? ' homepage-shortcut-heading--with-logo'
              : ''
          }`}
        >
          {shortcut.mainImage && (
            <div className="homepage-shortcut-icon-frame">
              <img
                className="homepage-shortcut-image"
                src={rewriteImageUrl(shortcut.mainImage)}
                alt=""
              />
            </div>
          )}
          <div className="homepage-shortcut-title-group">
            <div className="homepage-shortcut-subtitle">
              {unescape(shortcut.subtitle)}
            </div>
            <p className="homepage-shortcut-title">
              {unescape(shortcut.title)}
            </p>
          </div>
          {shortcut.secondaryImage && (
            <div className="homepage-shortcut-logo-frame">
              <img
                className="homepage-shortcut-logo"
                src={rewriteImageUrl(shortcut.secondaryImage)}
                alt=""
              />
            </div>
          )}
        </div>
        {shortcut.description && (
          <p className="homepage-shortcut-description">
            {unescape(shortcut.description)}
          </p>
        )}
        <div className="homepage-shortcut-actions">
          <ShortcutAction action={shortcut.primaryAction} />
          <ShortcutAction action={shortcut.secondaryAction} secondary />
        </div>
      </div>
    </article>
  ));

  return (
    <section className="page-section homepage-shortcuts" aria-label="Shortcuts">
      <div className="homepage-shortcuts-container" ref={containerRef}>
        {!showCarousel ? (
          <div
            className="card-list homepage-shortcuts-list"
            style={{
              gridTemplateColumns: `repeat(${visibleShortcutCount}, minmax(${SHORTCUT_MIN_WIDTH}px, 1fr))`,
            }}
          >
            {cards}
          </div>
        ) : (
          <div className="homepage-shortcuts-carousel edn-carousel edn-carousel-dots">
            <Carousel
              dots={showCarousel}
              infinite={false}
              slidesToShow={visibleShortcutCount}
              slidesToScroll={visibleShortcutCount}
              loading={false}
              arrows={false}
            >
              {cards}
            </Carousel>
          </div>
        )}
      </div>
    </section>
  );
};
